/**
 * One anatomical silhouette, painted per muscle.
 *
 * ── It draws the MOBILE app's figure, deliberately ────────────────────────────────────────────
 * The artwork is `react-native-body-highlighter` 3.2.0's, vendored into `bodyFront` / `bodyBack`
 * (owner, 12 August 2026: the web body must look like the phone's). What was here before was
 * `react-body-highlighter` 2.0.5 — a web port of the same project's FIRST model, drawing a visibly
 * different body — and a trainer who uses both surfaces saw two different clients.
 *
 * The package itself cannot be used here: it renders through `react-native-svg`. Its data can, and
 * does. So this component is the whole of what the package did that a browser needs — a `<svg>`, one
 * `<path>` per shape, a fill per slug — which is also why there is no wrapper around a library here
 * to delete later.
 *
 * ── Its interface is a colour per slug, and nothing else ──────────────────────────────────────
 * The caller says what each muscle is worth; this file knows where each muscle IS. Zones, sets and
 * thresholds stay in the caller's `model/`, and the figure has no opinion about them — which is why
 * the same component serves any surface that has a colour to put on a muscle.
 *
 * ── The figure is a picture — and, when a caller asks, a POINTER surface ────────────────────────
 * By default it carries no text, no roles and no handlers. Since 18 September 2026 (the exercise
 * creator's D21) a caller may pass `interactive`: the shapes it names answer a press and a hover,
 * nothing more. The figure stays `aria-hidden` either way — a caller that offers this must offer the
 * same act as a real control beside it (the creator's chips), because a shape is not focusable and
 * a listener cannot reach it. Every existing caller passes nothing and draws exactly as before.
 *
 * Without `interactive` it carries no text, no roles and no handlers. Every consumer draws labelled numbers beside it and
 * hides the drawing from assistive technology; a giant generated label would say the same thing
 * worse. Mobile takes the same decision, in the same words, for the same reason.
 */

import { bodySilhouetteArt, type BodyGender } from './bodySilhouetteArt';
import type { BodySlug } from './bodyArtwork';
import styles from './BodySilhouette.module.css';

interface BodySilhouetteProps {
  readonly side: 'front' | 'back';
  /**
   * Which body to draw. Named rather than defaulted: the caller knows whose client this is, and a
   * default here would quietly draw a man for every woman whose sex the read failed to carry.
   * `silhouetteGender` in the caller's `model/` is what turns a contract value into one of these.
   */
  readonly gender: BodyGender;
  /**
   * The fill for each muscle the caller has something to say about — a token reference, never a
   * literal. A slug this map does not carry takes `baseFill`, which is what makes an untrained
   * muscle and an undrawable one look the same: neutral.
   */
  readonly fillBySlug: Readonly<Partial<Record<BodySlug, string>>>;
  /** Everything else on the body: the head, the hands, and every muscle with no stimulus. */
  readonly baseFill: string;
  /**
   * Makes the named shapes answer the POINTER — a press and a hover. Optional and additive; see the
   * header for why the keyboard path must be the caller's own controls.
   */
  readonly interactive?: {
    readonly pressable: ReadonlySet<BodySlug>;
    readonly onPress: (slug: BodySlug) => void;
    readonly onHover: (slug: BodySlug | null) => void;
  };
}

export function BodySilhouette({
  side,
  gender,
  fillBySlug,
  baseFill,
  interactive,
}: BodySilhouetteProps) {
  const { viewBox, parts } = bodySilhouetteArt(gender, side);

  return (
    /* The box is the STYLESHEET's, and only the stylesheet's — `width: 100%` at
     * `height: var(--h-silhouette)`, in the module beside this file.
     *
     * This carried `width="112" height="224"` as well, as a belt against the frame where an inline
     * `<svg>` with only a `viewBox` falls back to the default object size. The belt is gone because
     * it does not hold: a presentation attribute loses to any CSS rule, so those numbers never
     * reached a styled frame, and nothing checked them against the token they claimed to restate.
     * The token moved to 258 with KANON v3 and the attributes stayed at 224 — a reserve 34px short
     * of the real one, which is a worse guard than none. One declaration, in one place, cannot
     * drift from itself. */
    <svg
      className={styles.figure}
      viewBox={viewBox}
      aria-hidden="true"
      focusable="false"
      onPointerLeave={interactive === undefined ? undefined : () => { interactive.onHover(null); }}
    >
      {parts.map((part) => {
        const fill = fillBySlug[part.slug] ?? baseFill;
        // `common`, then the two halves — the package's own order, and the order shapes overlap in.
        const paths = [
          ...(part.path.common ?? []),
          ...(part.path.left ?? []),
          ...(part.path.right ?? []),
        ];
        const pressable = interactive?.pressable.has(part.slug) === true;
        return paths.map((d) => (
          // Keyed by the path itself: the artwork is a frozen constant, so a shape's `d` is stable
          // and unique, and an index would tie the key to the order of a list nobody reorders.
          <path
            key={d}
            className={pressable ? `${String(styles.part)} ${String(styles.pressable)}` : styles.part}
            d={d}
            fill={fill}
            onClick={pressable ? () => { interactive?.onPress(part.slug); } : undefined}
            onPointerEnter={
              interactive === undefined
                ? undefined
                : () => {
                    interactive.onHover(pressable ? part.slug : null);
                  }
            }
          />
        ));
      })}
    </svg>
  );
}
